import { DateTime } from 'luxon';

export type DateRange = {
  from: DateTime;
  to: DateTime;
};

export type DateTimeFormat = DateTime<true> | DateTime<false> | string;

export type DateRangeFormat = {
  from: DateTimeFormat;
  to: DateTimeFormat;
};

export function dateTimeFrom(date: DateTimeFormat) {
  if (typeof date === 'string') {
    return DateTime.fromISO(date);
  }

  return date;
}

export function isInRange({
  includeLimits = true,
  range,
  date,
}: {
  includeLimits?: boolean;
  range: DateRangeFormat;
  date: DateTimeFormat;
}): boolean {
  const dateTime = dateTimeFrom(date).startOf('day');
  const from = dateTimeFrom(range.from).startOf('day');
  const to = dateTimeFrom(range.to).startOf('day');

  if (includeLimits) {
    return dateTime >= from && dateTime <= to;
  }

  return dateTime > from && dateTime < to;
}

export function isSameMonth(d1: DateTimeFormat | undefined, d2: DateTimeFormat | undefined): boolean {
  if (!d1 || !d2) {
    return false;
  }

  const dateTime1 = dateTimeFrom(d1);
  const dateTime2 = dateTimeFrom(d2);

  return dateTime1.hasSame(dateTime2, 'month') && dateTime1.hasSame(dateTime2, 'year');
}

export function isSameDate(d1: DateTimeFormat | undefined, d2: DateTimeFormat | undefined): boolean {
  if (!d1 || !d2) {
    return false;
  }

  const d1String = dateTimeFrom(d1).toFormat('yyyy-MM-dd');
  const d2String = dateTimeFrom(d2).toFormat('yyyy-MM-dd');

  return d1String === d2String;
}

export function isDateInRange(range: DateRangeFormat, d: DateTimeFormat): boolean {
  const date = dateTimeFrom(d).startOf('day');

  const from = dateTimeFrom(range.from).startOf('day');
  const to = dateTimeFrom(range.to).startOf('day');

  return date >= from && date <= to;
}

export function isDateInRanges(ranges: DateRangeFormat[], d: DateTimeFormat): boolean {
  const date = dateTimeFrom(d).startOf('day');

  return ranges.some(range => isDateInRange(range, date));
}

export function isDateInList(dates: DateTimeFormat[], d: DateTimeFormat): boolean {
  return dates.some(date => isSameDate(date, d));
}

export function isConsecutiveDates(d1: DateTime, d2: DateTime): boolean {
  return d2.diff(d1, 'days').days <= 1;
}

export function createRangesFromDates(dates: DateTimeFormat[]): DateRange[] {
  const sortedDates = dates.map(dateTimeFrom).sort((a, b) => a.toMillis() - b.toMillis());

  const dateRanges: DateRange[] = [];

  let rangeStart: DateTime | null = null;

  for (let i = 0; i < sortedDates.length; i++) {
    const current = sortedDates[i];
    const next = sortedDates[i + 1];

    if (!rangeStart) {
      rangeStart = current;
    }

    if (next && isConsecutiveDates(current, next)) {
      continue;
    }

    if (rangeStart && !isSameDate(rangeStart, current)) {
      dateRanges.push({ from: rangeStart, to: current });
    } else {
      dateRanges.push({ from: current, to: current });
    }

    rangeStart = null;
  }

  return dateRanges;
}
